import type { KeyboardEvent } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useSlotBattle } from '../../../../hooks/useSlotBattle';
import { useStores } from '../../../../stores/useStores';

/**
 * Legacy (pre-`ui_redesign_lobby`) rail entry for Battle of the Slots.
 *
 * Same gates and the same single tap target as the redesign rail's `SlotBattleWidget` — only the
 * markup differs, because this rail renders round nav icons rather than sized widget plates. The
 * artwork is `battle_run.widget_image_url`; there is no bundled fallback, so no image means no icon.
 *
 * The tap target stays the rail's shared `.circle-parent` div, so the icon sizes exactly like its
 * neighbours; it carries button semantics itself (name, focus, Enter/Space) instead of a `<button>`
 * whose UA styles the shared rail rules do not reset.
 */
export const SlotBattleNavWidget = observer(({ dataTestId = 'slot-battle-nav-widget' }: { dataTestId?: string }) => {
  const { t } = useTranslation();
  const { slotBattleStore } = useStores();
  const battle = useSlotBattle();

  if (!battle?.widgetImageUrl || !slotBattleStore.canShowWidget) {
    return null;
  }

  const open = () => slotBattleStore.openDialog('widget');

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      // Space would otherwise scroll the lobby.
      event.preventDefault();
      open();
    }
  };

  return (
    <div className="home-widget" data-testid={dataTestId}>
      <div
        aria-label={t('slotBattle.titleAlt')}
        className="circle-parent"
        data-testid={`${dataTestId}-button`}
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={handleKeyDown}
      >
        <div className="circle" />
        <img alt="" src={battle.widgetImageUrl} />
      </div>
    </div>
  );
});
